import React, { FC, ReactNode } from 'react';
import type {
  BillingInvoice,
  OrganisationBillingSummary,
  OrganisationMember,
} from '@dreamai/types';
import { ReactComponent as CardIcon } from '../../icons/card-add.svg';
import { ReactComponent as PlusIcon } from '../../icons/plus.svg';
import DreamMarque from '../common/DreamMarque';
import OrganisationInvoices from './OrganisationInvoices';
import { formatMonthly, formatPaymentDate } from './organisationBillingLabels';
import { formatMinor } from './organisationCreditLabels';

interface OrganisationSubscriptionProps {
  billing: OrganisationBillingSummary;
  teamAssistantSeats: OrganisationBillingSummary['teamAssistantSeats'];
  /** Active members with a Personal Assistant — the paid seats */
  seatMembers: OrganisationMember[];
  /** Finalised invoices; null while loading or when they could not be fetched */
  invoices: BillingInvoice[] | null;
  busy: boolean;
  /** The "Payment method" panel, shown once the subscription is live */
  paymentCard?: ReactNode;
  onSubscribe: () => void;
}

const LIVE_STATUSES = new Set([
  'CREATED',
  'IN_TRIAL',
  'ACTIVE',
  'PAST_DUE',
  'UNPAID',
]);

export function isSubscriptionLive(status: string | null): boolean {
  return !!status && LIVE_STATUSES.has(status);
}

const ATTENTION_LABELS: Record<string, string> = {
  CREATED: 'Starting up',
  PAST_DUE: 'Payment overdue — please check your card',
  UNPAID: 'Unpaid — please check your card',
};

const OrganisationSubscription: FC<OrganisationSubscriptionProps> = ({
  billing,
  teamAssistantSeats,
  seatMembers,
  invoices,
  busy,
  paymentCard,
  onSubscribe,
}) => {
  const plan = billing.seatPlan;
  const live = isSubscriptionLive(billing.subscriptionStatus);

  const membersLabel = (n: number) => `${n} ${n === 1 ? 'member' : 'members'}`;
  // A trial is opt-in per organisation (or per admin); while it runs the whole
  // organisation is free and the first payment bills every seat on the day it
  // ends.
  const inTrial = billing.subscriptionStatus === 'IN_TRIAL';
  const trialLabel = billing.trialDays
    ? `Free for ${billing.trialDays} ${billing.trialDays === 1 ? 'day' : 'days'}`
    : 'Free trial';
  const nextPaymentAt = (inTrial && billing.trialEndsAt) || billing.periodEnd;
  const attention = ATTENTION_LABELS[billing.subscriptionStatus ?? ''];
  const catchingUp =
    billing.billedQuantity !== null &&
    billing.billedQuantity !== billing.currentPlanUnits;

  // Before the first checkout there is no card; checkout saves it and starts
  // the subscription.
  const renderSetup = () => {
    const finished = billing.subscriptionStatus !== null;
    return (
      <>
        <div
          className='organisation-plan-setup'
          data-testid='organisation-subscription-needed'
        >
          <h4 className='organisation-plan-setup-title'>
            {finished
              ? 'Your subscription is finished'
              : 'Add payment details to invite your team'}
          </h4>
          <p className='organisation-plan-setup-text'>
            {finished
              ? 'Add a payment method below to keep using Dream.'
              : 'Manage all your organisation’s AI Secretaries in one place. Add a payment method below.'}
          </p>
          {plan && !finished && (
            <p
              className='organisation-plan-setup-text'
              data-testid='organisation-plan-pricing'
            >
              {billing.trialDays ? `${trialLabel}, then ` : ''}
              {formatMinor(plan.unitAmountMinor, plan.currency)} per member per
              month excl. VAT, billed monthly.
            </p>
          )}
          <button
            type='button'
            className='btn-primary organisation-plan-button'
            disabled={busy}
            onClick={onSubscribe}
            data-testid='organisation-subscribe'
          >
            {busy ? 'Opening…' : 'Set up payment method'}
          </button>
        </div>

        <h4 className='organisation-plan-heading'>Payment method</h4>
        <button
          type='button'
          className='organisation-payment-add'
          disabled={busy}
          onClick={onSubscribe}
        >
          <CardIcon aria-hidden='true' />
          <span>Add a payment method</span>
          <PlusIcon aria-hidden='true' />
        </button>

        <h4 className='organisation-plan-heading'>Invoices</h4>
        <OrganisationInvoices invoices={invoices} />
      </>
    );
  };

  const renderLive = () =>
    plan && (
      <>
        <div
          className='organisation-plan-panel'
          data-testid='organisation-bill'
        >
          <div className='organisation-plan-bubble' aria-hidden='true'>
            <DreamMarque />
          </div>
          <div className='organisation-plan-panel-body'>
            <p className='organisation-plan-eyebrow'>
              AI Secretary, Unlimited usage
            </p>
            <p className='organisation-plan-headline'>
              <span>
                {inTrial
                  ? trialLabel
                  : formatMonthly(
                      plan.unitAmountMinor * billing.currentPlanUnits,
                      plan.currency,
                    )}
              </span>
              <span
                className='organisation-plan-members'
                data-testid='organisation-subscription-members'
              >
                {membersLabel(seatMembers.length)}
              </span>
            </p>
            <div
              className='organisation-plan-seat-breakdown'
              data-testid='organisation-plan-seat-breakdown'
            >
              {teamAssistantSeats.map((assistant) => (
                <p
                  className='organisation-plan-seat-row'
                  data-testid='organisation-team-assistant-seat'
                  key={assistant.agentId}
                >
                  {assistant.name} — {assistant.seats}{' '}
                  {assistant.seats === 1 ? 'seat' : 'seats'} ({assistant.people}{' '}
                  people)
                </p>
              ))}
              <p
                className='organisation-plan-seat-total'
                data-testid='organisation-subscription-seat-total'
              >
                Total: {billing.currentPlanUnits} subscription{' '}
                {billing.currentPlanUnits === 1 ? 'seat' : 'seats'}
              </p>
            </div>
            {(attention || catchingUp) && (
              <p
                className='organisation-plan-caption'
                data-testid='organisation-subscription-status'
              >
                {attention}
                {attention && catchingUp && ' · '}
                {catchingUp && 'Your bill is updating shortly'}
              </p>
            )}
          </div>
          {nextPaymentAt && (
            <p className='organisation-plan-meta'>
              Next payment {formatPaymentDate(nextPaymentAt)}
            </p>
          )}
        </div>

        <h4 className='organisation-plan-heading'>Payment method</h4>
        {paymentCard}

        <h4 className='organisation-plan-heading'>Invoices</h4>
        <OrganisationInvoices invoices={invoices} />
      </>
    );

  return (
    <div
      className='organisation-card organisation-plan-card'
      data-testid='organisation-subscription'
    >
      <h3 className='organisation-card-title'>Plan &amp; billing</h3>

      {!plan && (
        <p className='organisation-notice'>
          Organisation billing is not configured on this server yet, so members
          cannot be invited. Contact Dream support.
        </p>
      )}

      {plan && (
        <div className='organisation-plan-heading-row'>
          <h4 className='organisation-plan-heading'>Your subscription</h4>
        </div>
      )}

      {plan && (live ? renderLive() : renderSetup())}
    </div>
  );
};

export default OrganisationSubscription;
